import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import DupontDatePicker from "../DatePopover/DupontDatePicker";
import CustomModal from "../../packages/CustomModal";
import DPCard from "../../packages/DPCard";
import DupontRadio from "../DupontRadio";
import {
  WPRangedInput,
  validationRangeHardError,
} from "../../packages/WPInput/WPRangedInput";
import CIPSection from "../../pages/defaultCIPInformation/CIPSection";
import WPTextArea from "../../packages/WPTextArea";

import { DatePickerConfig } from "../DatePopover/DateTimeConfig";
import { DURATION_CONFIG, ADD_INFO } from "./CIPEvent.config";
import {
  editCip,
  uploadCips,
  getPredictLimits,
  uploadRecurrentCips,
  getadvanceSettings,
} from "../../network/cipAPI";
import {
  DEFAULT_CIP_KEYS,
  CustomModalButtons,
  CIP_EVENT_TYPES,
  CIP_SOURCE_OPTIONS,
} from "../../constants/global.constants";
import { ValueStrings } from "../../utils/constantsString";
import {
  initializeDate,
  formatDate,
  convertMomentToDate,
} from "../../utils/Utils";
import createNotification, {
  SUCCESS_NOTIFICATION,
  ERROR_NOTIFICATION,
} from "../../components/Notifications";

import "./CIPEventDialog.scss";
import ConfirmationPopup, {
  DIALOG_TYPES,
} from "../../packages/ConfirmationPopup";
import { CIP_MISSING_VALIDATION } from "../../constants/global.constants";

const CIPEventDialog = ({
  editedRow,
  onModalClose,
  showLoader,
  refreshTable,
  source,
}) => {
  const { t } = useTranslation();

  const {
    acid_chem_id,
    alkali_chem_id,
    additional_chemical_chem_id,
    acid_price,
    alkali_price,
    additional_chemical_price,
    acid_enabled,
    alkali_enabled,
    additional_chemical_enabled,
    acid_used_per_cip,
    alkali_used_per_cip,
    additional_chemical_per_cip,
    acid_cip_ph,
    alkali_cip_ph,
    cip_solution_ph,
    acid_cip_temp,
    alkali_cip_temp,
    cip_solution_temp,
    acid_cip_duration,
    alkali_cip_duration,
    cip_solution_duration,
    add_info,
    selTrain: selectedTrain,
    minDaysBetweenCIPs,
    maxDaysBetweenCIPs,
  } = useSelector((state) => state);

  const [startDate, setStartDate] = useState(() =>
    editedRow ? initializeDate(editedRow.start_date) : null,
  );
  const [endDate, setEndDate] = useState(() =>
    editedRow ? initializeDate(editedRow.end_date) : null,
  );
  const [type, setType] = useState(
    editedRow && editedRow.type ? editedRow.type : "C",
  );
  const [daysBetweenCIPsRange, setDaysBetweenCIPsRange] = useState({
    min: minDaysBetweenCIPs,
    max: maxDaysBetweenCIPs,
  });

  const { duration_per_event, duration_between_event } = DURATION_CONFIG(
    t,
    CIP_EVENT_TYPES[type],
    daysBetweenCIPsRange,
  );

  const storeDefaultCipValues = {
    acid_chem_id,
    alkali_chem_id,
    additional_chemical_chem_id,
    acid_price,
    alkali_price,
    additional_chemical_price,
    acid_enabled,
    alkali_enabled,
    additional_chemical_enabled,
    acid_used_per_cip,
    alkali_used_per_cip,
    additional_chemical_per_cip,
    acid_cip_ph,
    alkali_cip_ph,
    cip_solution_ph,
    acid_cip_temp,
    alkali_cip_temp,
    cip_solution_temp,
    acid_cip_duration,
    alkali_cip_duration,
    cip_solution_duration,
    add_info,
  };

  const setCipModalInitialValues = () => ({
    type: "C",
    ...storeDefaultCipValues,
    duration_per_event: duration_per_event.default,
    duration_between_event: duration_between_event.default,
  });

  const [cipModal, setCipModal] = useState(
    editedRow || setCipModalInitialValues(),
  );

  const [errors, setErrors] = useState({
    acid_price: null,
    alkali_price: null,
    additional_chemical_price: null,
    acid_used_per_cip: null,
    alkali_used_per_cip: null,
    additional_chemical_per_cip: null,
    acid_cip_ph: null,
    alkali_cip_ph: null,
    cip_solution_ph: null,
    acid_cip_temp: null,
    alkali_cip_temp: null,
    cip_solution_temp: null,
    acid_cip_duration: null,
    alkali_cip_duration: null,
    cip_solution_duration: null,
    duration_per_event: null,
    duration_between_event: null,
  });

  const [minDateLimit, setMinDateLimit] = useState(null);
  const [maxDateLimit, setMaxDateLimit] = useState(null);
  const [startDateError, setStartDateError] = useState("");
  const [endDateError, setEndDateError] = useState("");
  const [noError, setNoError] = useState(true);
  const [loading, setLoading] = useState(false);
  const [isEditDataChanged, setIsEditDataChanged] = useState(false);
  const [showValidationWarning, setShowValidationWarning] = useState(false);
  const cipModalRef = useRef(cipModal);
  const hasInitializedCipDefaultsRef = useRef(false);

  const { DATE_TIME_FORMAT } = ValueStrings;

  const onClose = ({ target }) => {
    if (target.id === CustomModalButtons.CONFIRM) {
      saveEventHandler();
    } else {
      onModalClose();
    }
  };

  const saveEventHandler = async (forceSubmit = false) => {
    setLoading(true);
    try {
      const response = await (editedRow
        ? editCip(cipModal.cip_id, cipModal, selectedTrain)
        : source === CIP_SOURCE_OPTIONS.RECURRENT
          ? uploadRecurrentCips(cipModal, selectedTrain, forceSubmit ? 1 : 0)
          : uploadCips(cipModal, selectedTrain));
      if (response) {
        if (response.cip_id || response.status === 200) {
          onModalClose();
          const key = editedRow
            ? "CIPEvent.editedSuccess"
            : source === CIP_SOURCE_OPTIONS.RECURRENT
              ? "CIPEvent.recurrentSuccess"
              : "CIPEvent.addedSuccess";
          createNotification(
            SUCCESS_NOTIFICATION,
            t(key, {
              eventType: CIP_EVENT_TYPES[type],
            }),
          );
          // This will show the table (fetch CIP call will be triggered on initial mount, initial recurrent case)
          showLoader && showLoader(true);

          // explicitly fetch the CIPS (this is after paginated table mounted, more recurrent case)
          if (refreshTable) {
            await refreshTable();
          }
        } else {
          throw new Error(response.detail?.error);
        }
      }
    } catch (err) {
      setLoading(false);
      showLoader && showLoader(false);
      const missingCipsErrorMessage = err.message || err.Error || "";
      const isValidationError =
        missingCipsErrorMessage === CIP_MISSING_VALIDATION.ERROR_MESSAGE;

      if (isValidationError && !forceSubmit) {
        setShowValidationWarning(true);
        return;
      }

      const errorMsg = err.message || t("notificationStrings.somethingWrong");
      createNotification(ERROR_NOTIFICATION, errorMsg);
      onModalClose();
    }
  };

  const onStartDateChange = (value) => {
    const isoDate = formatDate(value);
    setStartDate(value);
    setStartDateError("");
    setCipModal((prev) => ({
      ...prev,
      start_date: isoDate,
      end_date: null,
    }));
    setEndDate(null);
    setEndDateError("");

    // if (Date.parse(value) >= Date.parse(endDate)) {
    //   setStartDateError(t("CIPEvent.startDateError"));
    // }
  };

  const onEndDateChange = (value) => {
    let newEndDate = new Date(value);
    const minDateTime = getMinDate();
    setEndDateError(""); // Clear previous error

    // Adjust time if it's less than minimum allowed
    if (minDateTime && newEndDate < minDateTime) {
      const isSameDay =
        newEndDate.toDateString() === minDateTime.toDateString();
      if (isSameDay) {
        newEndDate = new Date(minDateTime);
      }
    }

    // Validate against duration if we have start date and duration
    if (startDate && cipModal.duration_per_event) {
      const minDurationDateTime = calculateMinDateTime();

      if (minDurationDateTime && newEndDate < minDurationDateTime) {
        const formattedDateTime = formatDate(
          minDurationDateTime,
          "DD/MM/YYYY, hh:mm",
        );
        setEndDateError(
          t("CIPEvent.endDateDurationError", {
            minDateTime: formattedDateTime,
          }),
        );

        if (newEndDate.toDateString() === minDurationDateTime.toDateString()) {
          newEndDate = new Date(minDurationDateTime);
        }
      }
    }

    setEndDate(newEndDate);
    setCipModal((prev) => ({ ...prev, end_date: formatDate(newEndDate) }));
  };

  const handleRadioChange = (event) => {
    const { name } = event.target;
    setCipModal((prev) => ({ ...prev, type: name }));
    setType(name);
  };

  const handleInputAddChange = (key, value) => {
    setCipModal((prev) => ({ ...prev, [key]: value }));
  };

  const handleValidationWarningClose = (buttonId) => {
    setShowValidationWarning(false);
    if (buttonId === CustomModalButtons.CANCEL) {
      onModalClose();
      saveEventHandler(true);
    }
  };

  const handleInputChange = (field, value, minValue, maxValue) => {
    const validationError = getFieldValidationError(
      field,
      value,
      minValue,
      maxValue,
    );

    setErrors((prev) => ({ ...prev, [field]: validationError }));
    setCipModal((prev) => ({ ...prev, [field]: value }));

    // Check end date validation when duration changes
    if (field === "duration_per_event" && startDate && endDate) {
      const durationHours = parseFloat(value);
      if (!isNaN(durationHours)) {
        const minEndDateTime = new Date(startDate);
        const hoursPart = Math.floor(durationHours);
        const minutesPart = Math.round((durationHours - hoursPart) * 60);
        minEndDateTime.setHours(minEndDateTime.getHours() + hoursPart);
        minEndDateTime.setMinutes(minEndDateTime.getMinutes() + minutesPart);

        if (endDate < minEndDateTime) {
          setEndDateError(
            t("CIPEvent.endDateDurationError", {
              duration: `${value} ${t("defaultInformationStrings.hours")}`,
              minDateTime: formatDate(minEndDateTime, "DD/MM/YYYY, hh:mm"),
            }),
          );
        } else {
          setEndDateError("");
        }
      }
    }
  };

  const getFieldValidationError = useCallback(
    (_field, value, minValue, maxValue) => {
      if (minValue === null || minValue === undefined) {
        return null;
      }

      if (maxValue === null || maxValue === undefined) {
        return null;
      }

      const normalizedValue =
        value === null || value === undefined ? "" : String(value).trim();

      if (normalizedValue === "") {
        return t("ValueStrings.requiredField");
      }

      const numericValue = Number(normalizedValue);
      if (Number.isNaN(numericValue)) {
        return t("ValueStrings.requiredField");
      }

      const hardError = validationRangeHardError(t, numericValue, [
        minValue,
        maxValue,
      ]);

      return hardError?.error || null;
    },
    [t],
  );

  const handleSectionValidationToggle = useCallback(
    (_category, isEnabled, sectionFields = []) => {
      setErrors((prev) => {
        const nextErrors = { ...prev };

        sectionFields.forEach((field) => {
          const key = field?.key;
          if (!key) return;

          if (!isEnabled) {
            nextErrors[key] = null;
            return;
          }

          const value = cipModalRef.current?.[key];
          const minValue = field?.ranges?.hard?.min ?? null;
          const maxValue = field?.ranges?.hard?.max ?? null;

          nextErrors[key] = getFieldValidationError(
            key,
            value,
            minValue,
            maxValue,
          );
        });

        return nextErrors;
      });
    },
    [getFieldValidationError],
  );

  const heading = t("CIPEvent.heading", {
    actionType: editedRow ? t("CIPEvent.edit") : t("CIPEvent.add"),
    sourceType:
      source === CIP_SOURCE_OPTIONS.RECURRENT && !editedRow
        ? CIP_SOURCE_OPTIONS.RECURRENT
        : "",
  });

  const headerHelper = t("CIPEvent.description", {
    actionType: editedRow ? t("CIPEvent.edit") : t("CIPEvent.add"),
    sourceType:
      source === CIP_SOURCE_OPTIONS.RECURRENT && !editedRow
        ? CIP_SOURCE_OPTIONS.RECURRENT
        : "",
  });

  const CIPEventHeader = <div className="heading">{heading}</div>;
  const CIPEventHeaderHelper = (
    <div className="header-helper">{headerHelper}</div>
  );

  const fetchDateLimits = async () => {
    try {
      const limits = await getPredictLimits(selectedTrain);
      if (limits.data) {
        setMinDateLimit(convertMomentToDate(limits.data.min_date));
        setMaxDateLimit(convertMomentToDate(limits.data.max_date));
      } else {
        throw new Error("error");
      }
    } catch (err) {
      setLoading(false);
      createNotification(
        ERROR_NOTIFICATION,
        t("notificationStrings.somethingWrong"),
      );
      onModalClose();
    }
  };

  const fetchDurationLimits = async () => {
    try {
      const advanceSettings = await getadvanceSettings(selectedTrain);
      if (advanceSettings?.data?.settings) {
        const { min_day_between_cips, max_days_between_cips } =
          advanceSettings.data.settings;

        setDaysBetweenCIPsRange({
          min: min_day_between_cips,
          max: max_days_between_cips,
        });

        const durationConfig = DURATION_CONFIG(t, CIP_EVENT_TYPES[type], {
          min: min_day_between_cips,
          max: max_days_between_cips,
        });

        setCipModal((prev) => ({
          ...prev,
          duration_between_event: durationConfig.duration_between_event.default,
        }));
      } else {
        throw new Error("error");
      }
    } catch (err) {
      createNotification(
        ERROR_NOTIFICATION,
        t("notificationStrings.somethingWrong"),
      );
      onModalClose();
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 700);
    }
  };

  // Compare cipModal with editedRow for changes
  const editDataChangedHandler = () => {
    if (!editedRow) return false;

    const keys = Object.keys(cipModal);
    for (let key of keys) {
      if (key === "start_date" || key === "end_date") {
        if (formatDate(editedRow[key]) !== formatDate(cipModal[key])) {
          return true;
        }
      } else if ((editedRow[key] ?? "") != (cipModal[key] ?? "")) {
        return true;
      }
    }
    return false;
  };

  useEffect(() => {
    cipModalRef.current = cipModal;
  }, [cipModal]);

  useEffect(() => {
    const noError = Object.values(errors).every(
      (error) => error === "" || error === null,
    );
    setNoError(noError);
  }, [errors]);

  useEffect(() => {
    setLoading(true);
    if (source === CIP_SOURCE_OPTIONS.RECURRENT && !editedRow) {
      Promise.all([fetchDateLimits(), fetchDurationLimits()]);
    } else {
      fetchDateLimits().finally(() => setLoading(false));
    }
  }, []);

  useEffect(() => {
    if (editedRow || type !== "C" || hasInitializedCipDefaultsRef.current) {
      return;
    }

    hasInitializedCipDefaultsRef.current = true;

    setCipModal((prev) => ({
      ...prev,
      ...storeDefaultCipValues,
    }));
  }, [
    editedRow,
    type,
    acid_chem_id,
    alkali_chem_id,
    additional_chemical_chem_id,
    acid_price,
    alkali_price,
    additional_chemical_price,
    acid_enabled,
    alkali_enabled,
    additional_chemical_enabled,
    acid_used_per_cip,
    alkali_used_per_cip,
    additional_chemical_per_cip,
    acid_cip_ph,
    alkali_cip_ph,
    cip_solution_ph,
    acid_cip_temp,
    alkali_cip_temp,
    cip_solution_temp,
    acid_cip_duration,
    alkali_cip_duration,
    cip_solution_duration,
    add_info,
  ]);

  useEffect(() => {
    if (type !== "C") {
      hasInitializedCipDefaultsRef.current = false;
    }
  }, [type]);

  useEffect(() => {
    if (editedRow) {
      setIsEditDataChanged(editDataChangedHandler());
    }
  }, [cipModal, editedRow]);

  useEffect(() => {
    if (!editedRow) {
      if (type === "C") {
        setCipModal({
          ...setCipModalInitialValues(),
          start_date: startDate ? formatDate(startDate) : null,
          end_date: endDate ? formatDate(endDate) : null,
        });
      } else {
        setCipModal({
          type,
          start_date: startDate ? formatDate(startDate) : null,
          end_date: endDate ? formatDate(endDate) : null,
          duration_per_event: duration_per_event.default,
          duration_between_event: duration_between_event.default,
        });
      }
    }
  }, [type]);

  const calculateMinDateTime = () => {
    if (!startDate || !cipModal.duration_per_event) return null;

    const minDateTime = new Date(startDate);
    const durationHours = parseFloat(cipModal.duration_per_event);
    const totalMinutes = durationHours * 60;
    const hours = Math.floor(totalMinutes / 60);
    const minutes = Math.round(totalMinutes % 60);

    minDateTime.setHours(minDateTime.getHours() + hours);
    minDateTime.setMinutes(minDateTime.getMinutes() + minutes);
    return minDateTime;
  };

  const getMinDate = () => {
    if (!startDate) return startDate;
    return calculateMinDateTime() || startDate;
  };

  const getMinTime = () => {
    const minDateTime = calculateMinDateTime();
    if (!minDateTime) return undefined;

    // Only apply minTime if selected date is the same as calculated minimum date
    const selectedDate = endDate || new Date();
    return selectedDate.toDateString() === minDateTime.toDateString()
      ? minDateTime
      : undefined;
  };

  return (
    <CustomModal
      header={CIPEventHeader}
      headerHelper={CIPEventHeaderHelper}
      confirmBtn={t("CIPEvent.saveEvent")}
      cancelBtn={t("CIPEvent.close")}
      onModalClose={onClose}
      width="1100px"
      customclass="cip-event-dialog"
      confirmBtnDisabled={
        !startDate ||
        startDateError ||
        endDateError ||
        !noError ||
        (editedRow ? !isEditDataChanged : false) ||
        (source === CIP_SOURCE_OPTIONS.RECURRENT &&
          !editedRow &&
          (!cipModal.duration_per_event || !cipModal.duration_between_event))
      }
      isLoading={loading}
    >
      <DPCard>
        <div className="cip-event-fields">
          <div>
            <div className="field-name">{t("predictionStrings.type")}</div>
            <div className="radio-container" onChange={handleRadioChange}>
              <DupontRadio
                text={t("CIPEvent.CIP")}
                checked={type === "C"}
                onClick={() => setType("C")}
                name="C"
                value="C"
              />
              <DupontRadio
                text={t("CIPEvent.shutdown")}
                checked={type === "S"}
                onClick={() => setType("S")}
                name="S"
                value="S"
              />
            </div>
          </div>
          <div className="vertical-seperator"></div>
          <div className="date-fields-container">
            <DupontDatePicker
              selected={startDate}
              onChange={onStartDateChange}
              dateFormat={DATE_TIME_FORMAT}
              showTimeSelect={true}
              timeIntervals={15}
              timeFormat="HH:mm"
              showMonthDropdown={true}
              showYearDropdown={true}
              dropdownMode="select"
              minDate={minDateLimit}
              maxDate={maxDateLimit}
              label={`${CIP_EVENT_TYPES[type]} ${t("CIPEvent.startDate")}`}
              className={"text-left width-fill"}
              error={startDateError}
              isRequired
              placeholderText={t("CIPEvent.selectDateAndTime")}
              {...DatePickerConfig}
            />
            <DupontDatePicker
              selected={endDate}
              onChange={onEndDateChange}
              dateFormat={DATE_TIME_FORMAT}
              showTimeSelect={true}
              timeIntervals={15}
              timeFormat="HH:mm"
              showMonthDropdown={true}
              showYearDropdown={true}
              dropdownMode="select"
              label={`${CIP_EVENT_TYPES[type]} ${t("CIPEvent.endDate")}`}
              disabled={!Boolean(startDate)}
              error={endDateError}
              minDate={getMinDate()}
              minTime={getMinTime()}
              className={"width-fill"}
              placeholderText={t("CIPEvent.selectDateAndTime")}
              {...DatePickerConfig}
            />
          </div>

          {source === CIP_SOURCE_OPTIONS.RECURRENT && !editedRow && (
            <>
              <div className="vertical-seperator"></div>
              <div className="duration-fields-container">
                {[duration_per_event, duration_between_event].map((field) => (
                  <WPRangedInput
                    key={field.key}
                    value={cipModal[field.key]}
                    name={field.key}
                    label={field.label}
                    suffix={field.unit}
                    onChange={(e) =>
                      handleInputChange(
                        field.key,
                        e.target.value,
                        field.ranges?.hard?.min,
                        field.ranges?.hard?.max,
                      )
                    }
                    ranges={field.ranges}
                    maxLength={10}
                    required
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </DPCard>
      {type === "C" && (
        <div className="marginTop10">
          <CIPSection
            defaultCIPKeys={DEFAULT_CIP_KEYS}
            errors={errors}
            existingCIPState={cipModal}
            handleInputChange={handleInputChange}
            onFieldChange={handleInputAddChange}
            onSectionValidationToggle={handleSectionValidationToggle}
            idPrefix="recurrent_cip"
          />
        </div>
      )}
      <div className="margin-top-10">
        <WPTextArea
          value={cipModal[ADD_INFO.key]}
          label={t(`CIPEvent.addInfo`)}
          rows={ADD_INFO.multiline}
          onChange={(value) => handleInputAddChange(ADD_INFO.key, value)}
          maxCount={ADD_INFO.maxChar}
          placeholder={t(`CIPEvent.addInfoDefaultText`)}
        />
      </div>
      {showValidationWarning && (
        <ConfirmationPopup
          open={showValidationWarning}
          type={DIALOG_TYPES.WARNING}
          header={t("CIPEvent.missingCIPHeader")}
          description={t("CIPEvent.missingCIPDescription")}
          onClose={handleValidationWarningClose}
          cancelBtn={t("CIPEvent.missingCIPCancelBtn")}
          confirmBtn={t("CIPEvent.missingCIPConfirmBtn")}
          customBackdrop={true}
        ></ConfirmationPopup>
      )}
    </CustomModal>
  );
};

export default CIPEventDialog;
